import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Button,
    TextField,
    Divider,
    Alert,
    Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useAuth } from '../contexts/AuthContext';
import { useAppTheme } from '../App';
import { COLORS, SHADOWS, PRIMARY_ORANGE } from '../common/colors';

const Profile: React.FC = () => {
    const { currentUser, userData, updateUserProfile } = useAuth();
    const { isDarkMode, getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();

    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(userData?.fullName || '');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Function to get user initials
    const getUserInitials = () => {
        if (userData?.fullName) {
            const names = userData.fullName.trim().split(' ');
            if (names.length >= 2) {
                return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
            } else if (names.length === 1) {
                return names[0][0].toUpperCase();
            }
        }
        return currentUser?.email?.[0]?.toUpperCase() || 'U';
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Cancel editing
            setEditedName(userData?.fullName || '');
            setIsEditing(false);
            setErrorMessage('');
        } else {
            // Start editing
            setIsEditing(true);
            setSuccessMessage('');
        }
    };

    const handleSave = async () => {
        if (!editedName.trim()) {
            setErrorMessage('Full name cannot be empty');
            return;
        }

        try {
            setIsLoading(true);
            setErrorMessage('');

            await updateUserProfile({ fullName: editedName.trim() });

            setIsEditing(false);
            setSuccessMessage('Profile updated successfully!');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error updating profile:', error);
            setErrorMessage('Failed to update profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Unknown';
        }
    };

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 64px)',
                background: theme.background,
                p: 3,
            }}
        >
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {/* Header */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        color: theme.textPrimary,
                        mb: 1,
                        textAlign: 'center',
                    }}
                >
                    Profile Settings
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: theme.textSecondary,
                        textAlign: 'center',
                        mb: 4,
                    }}
                >
                    Manage your account information and preferences
                </Typography>

                {/* Success/Error Messages */}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                        {successMessage}
                    </Alert>
                )}
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {/* Profile Card */}
                <Card
                    sx={{
                        borderRadius: 4,
                        background: theme.cardBackground,
                        boxShadow: `${SHADOWS.card} ${theme.shadowColor}`,
                        backdropFilter: 'blur(16px)',
                        border: `1px solid ${theme.borderColor}`,
                        mb: 3,
                    }}
                    elevation={0}
                >
                    <CardContent sx={{ p: 4 }}>
                        {/* Profile Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    mr: 3,
                                    backgroundColor: COLORS.primary,
                                    color: 'white',
                                    fontSize: '2rem',
                                    fontWeight: 600,
                                    boxShadow: SHADOWS.primary,
                                }}
                            >
                                {getUserInitials()}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.textPrimary,
                                            mr: 2,
                                        }}
                                    >
                                        {userData?.fullName || 'User'}
                                    </Typography>
                                    {currentUser?.emailVerified && (
                                        <Chip
                                            icon={<VerifiedIcon />}
                                            label="Verified"
                                            size="small"
                                            sx={{
                                                background: 'rgba(34, 197, 94, 0.1)',
                                                color: '#22c55e',
                                                border: '1px solid rgba(34, 197, 94, 0.2)',
                                            }}
                                        />
                                    )}
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.textSecondary,
                                        mb: 1,
                                    }}
                                >
                                    {currentUser?.email}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.textSecondary,
                                        opacity: 0.8,
                                    }}
                                >
                                    Member since {formatDate(currentUser?.metadata?.creationTime || '')}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 4, borderColor: theme.borderColor }} />

                        {/* Profile Information */}
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: theme.textPrimary,
                                mb: 3,
                            }}
                        >
                            Personal Information
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Full Name */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PersonIcon sx={{ color: theme.textSecondary, mr: 1 }} />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.textPrimary,
                                        }}
                                    >
                                        Full Name
                                    </Typography>
                                </Box>
                                {isEditing ? (
                                    <TextField
                                        fullWidth
                                        value={editedName}
                                        onChange={(e) => setEditedName(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                background: isDarkMode ? 'rgba(64, 65, 79, 0.3)' : 'rgba(255, 255, 255, 1)',
                                                '& fieldset': {
                                                    border: `1px solid ${theme.borderColor}`,
                                                },
                                                '&:hover fieldset': {
                                                    border: `1px solid ${PRIMARY_ORANGE.main}`,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: `2px solid ${PRIMARY_ORANGE.main}`,
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: theme.textPrimary,
                                            },
                                        }}
                                    />
                                ) : (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.textPrimary,
                                            p: 1.5,
                                            background: isDarkMode ? 'rgba(64, 65, 79, 0.2)' : 'rgba(247, 247, 248, 0.8)',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.borderColor}`,
                                        }}
                                    >
                                        {userData?.fullName || 'Not set'}
                                    </Typography>
                                )}
                            </Box>

                            {/* Email */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <EmailIcon sx={{ color: theme.textSecondary, mr: 1 }} />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.textPrimary,
                                        }}
                                    >
                                        Email Address
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.textPrimary,
                                        p: 1.5,
                                        background: isDarkMode ? 'rgba(64, 65, 79, 0.2)' : 'rgba(247, 247, 248, 0.8)',
                                        borderRadius: 2,
                                        border: `1px solid ${theme.borderColor}`,
                                        opacity: 0.8,
                                    }}
                                >
                                    {currentUser?.email}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.textSecondary,
                                        mt: 0.5,
                                        display: 'block',
                                    }}
                                >
                                    Email cannot be changed
                                </Typography>
                            </Box>

                            {/* Account Created */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <CalendarTodayIcon sx={{ color: theme.textSecondary, mr: 1 }} />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.textPrimary,
                                        }}
                                    >
                                        Account Created
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.textPrimary,
                                        p: 1.5,
                                        background: isDarkMode ? 'rgba(64, 65, 79, 0.2)' : 'rgba(247, 247, 248, 0.8)',
                                        borderRadius: 2,
                                        border: `1px solid ${theme.borderColor}`,
                                        opacity: 0.8,
                                    }}
                                >
                                    {formatDate(currentUser?.metadata?.creationTime || '')}
                                </Typography>
                            </Box>

                            {/* Email Verification Status */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <VerifiedIcon sx={{ color: theme.textSecondary, mr: 1 }} />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.textPrimary,
                                        }}
                                    >
                                        Email Status
                                    </Typography>
                                </Box>
                                <Chip
                                    label={currentUser?.emailVerified ? 'Verified' : 'Not Verified'}
                                    sx={{
                                        background: currentUser?.emailVerified
                                            ? 'rgba(34, 197, 94, 0.1)'
                                            : 'rgba(239, 68, 68, 0.1)',
                                        color: currentUser?.emailVerified ? '#22c55e' : '#ef4444',
                                        border: currentUser?.emailVerified
                                            ? '1px solid rgba(34, 197, 94, 0.2)'
                                            : '1px solid rgba(239, 68, 68, 0.2)',
                                        fontWeight: 500,
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
                            {isEditing ? (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={handleEditToggle}
                                        disabled={isLoading}
                                        sx={{
                                            borderColor: theme.textSecondary,
                                            color: theme.textSecondary,
                                            borderRadius: 2,
                                            '&:hover': {
                                                borderColor: theme.textPrimary,
                                                color: theme.textPrimary,
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSave}
                                        disabled={isLoading}
                                        sx={{
                                            backgroundColor: COLORS.primary,
                                            color: 'white',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            boxShadow: SHADOWS.primary,
                                            '&:hover': {
                                                backgroundColor: COLORS.primaryHover,
                                            },
                                            '&:disabled': {
                                                background: theme.textSecondary,
                                                opacity: 0.6,
                                            },
                                        }}
                                    >
                                        {isLoading ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={handleEditToggle}
                                    sx={{
                                        backgroundColor: COLORS.primary,
                                        color: 'white',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: SHADOWS.primary,
                                        '&:hover': {
                                            backgroundColor: COLORS.primaryHover,
                                        },
                                    }}
                                >
                                    Edit Profile
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Profile; 